import {
  MenuItem,
  Typography,
  Select,
  SelectChangeEvent,
  FormControlLabel,
  Checkbox,
  Box,
} from '@mui/material'
import { test, enforce, create } from 'vest'
import { IQuestionV2, MainObject } from '../../store/reports/types'
import CardTAG from '../CardTAG'
import { IOptionItem } from '../Dropdown/types'
import selectOptions from '../../constants/selectOptions'
import colors from '../../constants/colors'
import { ReactComponent as ArrowIcon } from '../../assets/images/arrowIcon.svg'
import { questionParser } from '../NewCase'
import LabelErrorTAG from '../LabelErrorTAG'
import DividerTAG from '../DividerTAG'

const selectStyle = {
  '&.MuiSelect-outlined , .MuiOutlinedInput-input': {
    p: '.8rem',
    marginTop: 0,
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.WHITE,
  },
  '&.MuiSelect-root , .MuiSelect-icon': {
    top: 'auto !important',
    right: '1.2rem',
    fill: colors.TAG_LINES_GRAY,
  },
  minWidth: '20rem',
  width: '100%',
  height: '100%',
  borderRadius: 0,
  justifyContent: 'center',
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '.MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}

export const formValidation = create((data = {}) => {
  const { firstName, lastName, email, title } = data

  test('firstName', 'You must type first name', () => {
    enforce(firstName).isNotEmpty()
  })

  test('lastName', 'You must type last name', () => {
    enforce(lastName).isNotEmpty()
  })

  test('email', 'Email must be valid!', () => {
    enforce(email).isValidEmail()
  })
  test('title', 'You must select User Title', () => {
    enforce(title).isNotEmpty()
  })
})

const isPlural = (count: number) => (count === 1 ? '' : 's')

export const resolveNumberOfChangesString = (changes: number) => {
  if (changes == 0) return ''
  if (changes > 9) return `9+ Edit${isPlural(changes)}`
  return `${changes} Edit${isPlural(changes)}`
}

export const makeid = () => {
  let result = ''
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  const charactersLength = characters.length
  let counter = 0
  while (counter < 6) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength))
    counter += 1
  }
  return result
}

export const renderSelectOptions = (options: IOptionItem[]) => {
  return options.map((selectOption, index) => {
    return (
      <MenuItem value={selectOption.value} key={`${selectOption.label}-${index}`}>
        <Typography variant='h4'>{selectOption.label}</Typography>
      </MenuItem>
    )
  })
}

export const findItemByUuid = (items: MainObject[], uuid: any): MainObject | null => {
  if (!items || !Array.isArray(items)) {
    return null
  }
  for (const item of items) {
    if (item?.uuid === uuid) {
      return item
    }
    const subitem = findItemByUuid(item?.subanswers, uuid)
    if (subitem) {
      return subitem
    }
  }
  return null
}

export const updateAnswersText = (
  arrayOfAnswers: any[],
  answerUuid: string,
  value: string,
  answerId?: number,
) => {
  const updatedArray: { uuid: string; answer: { text: string }; subanswers?: any[] }[] =
    arrayOfAnswers.map(answer => {
      if (answer?.uuid === answerUuid) {
        answer.answer.text = value
        const newObject = {
          ...answer,
          ...(answerId ? { id: answerId } : {}),
          answer: {
            ...answer.answer,
            text: value,
          },
        }
        return newObject
      } else if (answer?.subanswers) {
        return {
          ...answer,
          subanswers: updateAnswersText(answer?.subanswers, answerUuid, value, answerId),
        }
      }

      return { ...answer }
    })
  return [...updatedArray]
}

export const updateAnswersOptions = (
  arrayOfAnswers: any[],
  answerUuid: string,
  value: string[],
  answerId?: number,
) => {
  const updatedArray: { uuid: string; answer: { text: string }; subanswers?: any[] }[] =
    arrayOfAnswers.map(answer => {
      if (answer?.uuid === answerUuid) {
        answer.options = value
        return {
          ...answer,
          options: value,
          ...(answerId ? { id: answerId } : {}),
        }
      } else if (answer?.subanswers) {
        return {
          ...answer,
          subanswers: updateAnswersOptions(answer?.subanswers, answerUuid, value, answerId),
        }
      }

      return { ...answer }
    })

  return [...updatedArray]
}

const logicalQuestionHandler = (
  question: IQuestionV2,
  oshaAnswers: any,
  setOshaAnswers: any,
  previewOnly = false,
) => {
  const findAnswer = findItemByUuid(oshaAnswers, question?.uuid)
  const handleLogicQuestionChange = (e: SelectChangeEvent) => {
    const updatedAnswers = oshaAnswers.map((answer: { uuid: string; answer: { text: string } }) => {
      if (answer?.uuid === question?.uuid) {
        answer.answer.text = e.target.value
        return answer
      }
    })
    if (updatedAnswers) {
      setOshaAnswers(updatedAnswers)
    }
  }

  return (
    <div id={`${question?.id ?? Math.random()}`} className='marginTop-sections'>
      <CardTAG
        title={question?.text}
        cardAction={
          <Select
            onChange={handleLogicQuestionChange}
            value={findAnswer?.answer?.text}
            IconComponent={ArrowIcon}
            displayEmpty
            renderValue={
              findAnswer?.answer?.text !== ''
                ? undefined
                : () => (
                    <Typography variant='bodyRegular' color={colors.TAG_TEXT_GRAY}>
                      {'Select a value'}
                    </Typography>
                  )
            }
            sx={selectStyle}
            disabled={previewOnly}
          >
            {renderSelectOptions(selectOptions.yesNo)}
          </Select>
        }
      />
      {findAnswer?.answer?.text === 'YES' &&
        question?.subquestions.map(subQuestion => {
          return oshaQuestionParser(subQuestion, oshaAnswers, setOshaAnswers, previewOnly)
        })}
    </div>
  )
}

const NumberQuestionHandler = (
  question: IQuestionV2,
  oshaAnswers: any,
  setOshaAnswers: any,
  previewOnly = false,
) => {
  const findAnswer = findItemByUuid(oshaAnswers, question?.uuid)

  const handleNumberQuestionChange = (e: SelectChangeEvent) => {
    const updatedAnswers = updateAnswersText(oshaAnswers, question?.uuid, e?.target?.value)
    if (updatedAnswers) {
      setOshaAnswers(updatedAnswers)
    }
  }

  return (
    <div id={`${question?.id ?? Math.random()}`} className='marginTop-sections'>
      <CardTAG
        title={question?.text}
        cardAction={
          <input
            className='NumberInput-container'
            type='number'
            value={findAnswer?.answer?.text}
            onChange={handleNumberQuestionChange}
            min={0}
            disabled={previewOnly}
          />
        }
      />
    </div>
  )
}

const SingleSelectQuestionHandler = (
  question: IQuestionV2,
  oshaAnswers: any,
  setOshaAnswers: any,
  previewOnly = false,
) => {
  const findAnswer = findItemByUuid(oshaAnswers, question?.uuid)
  const optionsAndSubquestions = question?.options.map((option, index) => {
    const found = question?.subquestions.filter(subQuestion => subQuestion?.option === option.id)
    return found?.length > 0
      ? {
          option,
          subquestions: found,
        }
      : {
          option,
        }
  })

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event?.target?.checked) {
      // find answer and add array of options
      const updatedAnswers = updateAnswersOptions(oshaAnswers, question?.uuid, [
        event?.target?.value,
      ])
      if (updatedAnswers) {
        setOshaAnswers(updatedAnswers)
        return
      }
    }
    const updatedAnswers = updateAnswersOptions(oshaAnswers, question?.uuid, [])
    if (updatedAnswers) {
      setOshaAnswers(updatedAnswers)
    }
  }
  const resolveChecked = (optionId: number) => {
    if (!findAnswer) return false
    return findAnswer?.options?.filter(id => id == optionId).length > 0
  }

  const renderOptions = () => {
    return optionsAndSubquestions.map(option => {
      const hasChildQuestions = Object.prototype.hasOwnProperty.call(option, 'subquestions')

      if (!hasChildQuestions) {
        return (
          <Box sx={{ width: '100%' }} key={`${option?.option?.id}`}>
            <FormControlLabel
              control={
                <Checkbox
                  value={option?.option.id}
                  onChange={onChangeHandler}
                  checked={resolveChecked(option?.option.id)}
                  disabled={previewOnly}
                />
              }
              label={option?.option.text}
            />
            <DividerTAG />
          </Box>
        )
      }

      return (
        <Box sx={{ width: '100%' }} key={option?.option?.id}>
          <FormControlLabel
            control={
              <Checkbox
                value={option?.option.id}
                onChange={onChangeHandler}
                checked={resolveChecked(option?.option.id)}
                disabled={previewOnly}
              />
            }
            label={option?.option.text}
          />
          <Box sx={{ width: '100%', paddingLeft: '2rem' }}>
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              {resolveChecked(option?.option.id) &&
                option?.subquestions?.map(subquestion => questionParser(subquestion))}
            </Box>
          </Box>
          <DividerTAG />
        </Box>
      )
    })
  }
  return (
    <div id={`${question?.id ?? Math.random()}`} className='marginTop-sections'>
      <CardTAG title={question?.text}>
        <LabelErrorTAG label={question?.variant?.label} />
        {renderOptions()}
      </CardTAG>
    </div>
  )
}

const questionTypes: { [key: string]: any } = {
  LOGICAL: logicalQuestionHandler,
  NUMBER: NumberQuestionHandler,
  SINGLE_SELECT: SingleSelectQuestionHandler,
}

export const oshaQuestionParser = (
  question: IQuestionV2,
  oshaAnswers: any,
  setOshaAnswers: any,
  previewOnly = false,
) => {
  const questionVariantType = question?.variant?.type
  if (questionVariantType in questionTypes) {
    return questionTypes[questionVariantType](question, oshaAnswers, setOshaAnswers, previewOnly)
  }
  return <></>
}

/* eslint-disable */
export function transformStructure(structure: any, subOptionId = undefined) {
  // Initialize the transformed structure
  const transformed = {
    id: undefined,
    answer: {
      question: structure?.id,
      text: '',
    },
    options: [],
    subanswers: [],
    uuid: structure?.uuid,
    ...(subOptionId ? { option: subOptionId } : {}),
  }

  // If the structure has subquestions, transform them recursively
  if (structure?.subquestions.length > 0) {
    transformed.subanswers = structure?.subquestions.map((subquestion: any) => {
      const optionId = structure?.options[subquestion?.option - 1]?.id
      return transformStructure(subquestion, optionId)
    })
  }

  return transformed
}

export const getIdsFromUsers = (users: any) => Object.keys(users)?.map(key => users[key]?.id)
